import React from 'react';
import {
  Box,
  Text,
  HStack,
  Stack,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverArrow,
  useColorModeValue,
  Portal,
  Icon,
} from '@chakra-ui/react';
import { CheckCircleIcon, InfoOutlineIcon } from '@chakra-ui/icons';

export interface HelpTooltipCardProps {
  label: string;
  title: string;
  items?: string[];
  showStrengthBars?: boolean;
}

/**
 * HelpTooltipCard
 *
 * Reusable rich tooltip/popover used for contextual help next to form fields.
 * Triggered by a small pill with label text; shows a card with colored bars
 * and a checklist inside, visually similar to password requirement examples.
 */
export const HelpTooltipCard: React.FC<HelpTooltipCardProps> = ({ label, title, items, showStrengthBars = false }) => {
  const bg = useColorModeValue('white', 'gray.800');
  const border = useColorModeValue('gray.200', 'gray.700');
  const pillBg = useColorModeValue('white', 'gray.700');
  const pillBorder = useColorModeValue('gray.200', 'gray.600');
  const iconColor = useColorModeValue('gray.600', 'gray.200');

  return (
    <Popover trigger="hover" openDelay={150} closeDelay={100} placement="right-start">
      <PopoverTrigger>
        <Box
          as="button"
          type="button"
          borderRadius="full"
          bg={pillBg}
          borderWidth="1px"
          borderColor={pillBorder}
          boxShadow="sm"
          fontSize="xs"
          fontWeight="medium"
          _hover={{ boxShadow: 'md', transform: 'translateY(-1px)' }}
          transition="all 0.15s ease-out"
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          w="18px"
          h="18px"
          aria-label={label}
        >
          <Icon as={InfoOutlineIcon} boxSize={3} color={iconColor} />
        </Box>
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          maxW="sm"
          bg={bg}
          borderColor={border}
          boxShadow="2xl"
          borderRadius="xl"
          _focus={{ boxShadow: '2xl' }}
        >
          <PopoverArrow />
          <PopoverBody p={4}>
            <Stack spacing={3}>
              {showStrengthBars && (
                <HStack spacing={1.5}>
                  <Box flex="1" h="2px" borderRadius="full" bg="red.400" />
                  <Box flex="1" h="2px" borderRadius="full" bg="orange.400" />
                  <Box flex="1" h="2px" borderRadius="full" bg="green.400" />
                </HStack>
              )}

              <Text fontSize="sm" fontWeight="semibold">
                {title}
              </Text>

              {items && items.length > 0 && (
                <Stack as="ul" spacing={1.5} pl={0} m={0} style={{ listStyle: 'none' }}>
                  {items.map((item) => (
                    <HStack as="li" key={item} align="flex-start" spacing={2}>
                      <Icon as={CheckCircleIcon} color="green.400" boxSize={3.5} mt={0.5} />
                      <Text fontSize="sm" color="gray.700" _dark={{ color: 'gray.200' }}>
                        {item}
                      </Text>
                    </HStack>
                  ))}
                </Stack>
              )}
            </Stack>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

export const PasswordHelpTooltip: React.FC<{ label?: string }> = ({ label = 'Požadavky na heslo' }) => {
  return (
    <HelpTooltipCard
      label={label}
      title="Silné heslo by mělo obsahovat:"
      items={[
        'Minimálně 8 znaků (povinné)',
        'Kombinaci malých a velkých písmen',
        'Čísla a ideálně speciální znaky',
      ]}
      showStrengthBars
    />
  );
};

export default HelpTooltipCard;
